import { BugReportFormMetadata } from '@actions/WogaaLayerActions';
import { EnvConfigs } from '@environment/resources';
import { Logger } from './Logger';
import appStore from '@stores/app.store';
import { ServiceType } from '@entities/ServiceType';

export class BugReportService {
  private http: typeof fetch;
  private currentController = {} as AbortController;
  private Bug_Report_Name: string;
  constructor() {
    this.http = async (url, options: RequestInit = {}) => {
      // Cancel any ongoing request
      if (Object.keys(this.currentController).length) {
        this.currentController.abort();
      }

      // Create a new controller for this request
      const controller = new AbortController();
      this.currentController = controller;

      try {
        const response = await fetch(url, { ...options, signal: controller.signal });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`)
        };

        const data = await response.json();
        this.currentController = {} as AbortController;
        return data;
      } catch (error: any) {
        this.currentController = {} as AbortController;

        if (error.name === 'AbortError') {
          Logger.log(`Error while requesting in ${url}`, error);
          throw new Error(`Error while processing request`);
        }
        Logger.log('Network error:', error);
        throw new Error('Network error');
      }
    };

    this.Bug_Report_Name = 'sentiments-bug-report';
  }

  private resetBugReportId() {
    window.sessionStorage.removeItem(this.Bug_Report_Name);
  }

  private setBugReportId(bugId: string) {
    window.sessionStorage.setItem(this.Bug_Report_Name, bugId);
  }

  private getBugReportId() {
    return window.sessionStorage.getItem(this.Bug_Report_Name);
  }

  private getUrl(path: string, searchParams?: Record<string, any>): string {
    const search = new URLSearchParams(searchParams);
    const paths = [EnvConfigs.SentimentsEndpoint, path].join('/');
    const url = new URL(paths);
    url.search = search.toString();
    return url.toString();
  }

  private getBrowserInfo() {
    const ua = navigator.userAgent;
    let browser = 'Unknown';
    let version = 'Unknown';

    const match = (regex: RegExp) => ua.match(regex);

    if (match(/GSA\/([\d.]+)/)) {
      browser = 'Google App';
      version = (match(/GSA\/([\d.]+)/) as RegExpMatchArray)[1];
    }
    else if (match(/OPR\/([\d.]+)/)) {
      browser = 'Opera';
      version = (match(/OPR\/([\d.]+)/) as RegExpMatchArray)[1];
    } else if (match(/Edg\/([\d.]+)/)) {
      browser = 'Edge';
      version = (match(/Edg\/([\d.]+)/) as RegExpMatchArray)[1];
    } else if (match(/Chrome\/([\d.]+)/)) {
      browser = 'Chrome';
      version = (match(/Chrome\/([\d.]+)/) as RegExpMatchArray)[1];
    } else if (match(/Firefox\/([\d.]+)/)) {
      browser = 'Firefox';
      version = (match(/Firefox\/([\d.]+)/) as RegExpMatchArray)[1];
    } else if (match(/Version\/(\d+(\.\d+)?).*Safari/)) {
      browser = 'Safari';
      version = (match(/Version\/([\d.]+).*Safari/) as RegExpMatchArray)[1];
    } else if (match(/SamsungBrowser\/([\d.]+)/)) {
      browser = 'Samsung Internet';
      version = (match(/SamsungBrowser\/([\d.]+)/) as RegExpMatchArray)![1];
    } else if (match(/CriOS\/([\d.]+)/)) {
      browser = 'Chrome (iOS)';
      version = (match(/CriOS\/([\d.]+)/) as RegExpMatchArray)![1];
    }

    return `${browser}-${version}`;
  }
  private getClientContext() {
    const ua = navigator.userAgent;

    // browser
    let browser = this.getBrowserInfo();

    // OS
    let osDevice = 'Unknown';
    if (ua.includes('Windows')) osDevice = 'Windows';
    else if (ua.includes('Mac OS')) osDevice = 'Mac OS';
    else if (ua.includes('Android')) osDevice = 'Android';
    else if (ua.includes('iPhone') || ua.includes('iPad')) osDevice = 'iOS';
    else if (ua.includes('Linux')) osDevice = 'Linux';

    // device type
    let deviceType = 'desktop';
    if (/Mobi|Android/i.test(ua)) deviceType = 'mobile';
    if (/Tablet|iPad/i.test(ua)) deviceType = 'tablet';

    // full URL
    const serviceUrl = window.location.href;

    return {
      serviceUrl,
      browser,
      osDevice,
      deviceType,
    };
  }

  // Convert Base64 key to CryptoKey
  private async importKey() {
    const base64Key = EnvConfigs.BrtSecurityKey;
    const rawKey = Uint8Array.from(atob(base64Key), c => c.charCodeAt(0));
    return window.crypto.subtle.importKey(
      'raw',
      rawKey,
      { name: EnvConfigs.BrtSecurityAlgorithm },
      false, // extractable
      ['encrypt', 'decrypt'],
    );
  }

  // Encrypt a string
  private async encrypt(plainText: string) {
    const key = await this.importKey();
    const encoder = new TextEncoder();
    const data = encoder.encode(plainText);

    // Generate a random 12-byte IV (recommended for GCM)
    const iv = window.crypto.getRandomValues(new Uint8Array(12));

    const ciphertext = await window.crypto.subtle.encrypt(
      {
        name: EnvConfigs.BrtSecurityAlgorithm,
        iv: iv,
      },
      key,
      data,
    );

    // Return IV + ciphertext concatenated and Base64-encoded
    const combined = new Uint8Array(iv.length + ciphertext.byteLength);
    combined.set(iv);
    combined.set(new Uint8Array(ciphertext), iv.length);

    return btoa(String.fromCharCode(...combined));
  }

  private async decrypt(encryptedBase64: string): Promise<string> {
    const key = await this.importKey();
    const combined = Uint8Array.from(atob(encryptedBase64), c => c.charCodeAt(0));

    const iv = combined.slice(0, 12);
    const ciphertext = combined.slice(12, -16); // last 16 bytes are auth tag
    const authTag = combined.slice(-16);

    const decrypted = await window.crypto.subtle.decrypt(
      {
        name: EnvConfigs.BrtSecurityAlgorithm,
        iv: iv,
        tagLength: 128, // 16 bytes
      },
      key,
      // The Web Crypto API expects ciphertext + authTag concatenated
      (() => {
        const concat = new Uint8Array(ciphertext.length + authTag.length);
        concat.set(ciphertext);
        concat.set(authTag, ciphertext.length);
        return concat;
      })(),
    );

    const decoder = new TextDecoder();
    return decoder.decode(decrypted);
  }

  /**
   * get brt config
   * @param serviceType
   * @param trackingId
   * @returns
   */
  GetBRTConfig(serviceType: ServiceType, trackingId?: string) {
    const configuration = appStore.get('configuration');
    if (configuration) {
      const { bugReportTool } = configuration;
      if (!bugReportTool) {
        return { isEnabled: false };
      }

      const brtConfig =
        serviceType === ServiceType.InformationalService
          ? configuration?.bugReportTool.informationalService
          : configuration?.bugReportTool.transactionalServices.filter(
              e => e.trackingId === trackingId,
            )[0];
      return brtConfig ? brtConfig : { isEnabled: false };
    }
    return { isEnabled: false };
  }

  /**
   * equest to sentiments backend to get pre-sign url for uploading.
   *
   * @param sentimentsId
   * @param imageType
   * @returns
   */
  async RequestPreSignedURL(
    rsid: string,
    sentimentsId: string,
    imageType: string,
  ): Promise<Record<string, string>> {
    try {
      const url = this.getUrl(`public/v1/brt/response/${rsid}/upload-url`, {
        transactionId: sentimentsId,
        contentType: imageType,
      });
      const response = await this.http(url, { method: 'GET' });

      const decryptedData = JSON.parse(await this.decrypt((response as any).data));

      this.setBugReportId(decryptedData?.bugId);

      return decryptedData;
    } catch (error) {
      Logger.log('error while requesting S3 presigned url', { error });
      throw new Error('error while requesting S3 presigned url');
    }
  }

  /**
   * upload image to S3 storage via pre-sign url.
   *
   * @param preSignedUrl
   * @param file
   * @returns
   */
  async UploadImage(preSignedUrl: string, file: Blob | null, imageType: string): Promise<void> {
    try {
      const data = await fetch(preSignedUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': imageType,
        },
        body: file,
      });

      if (!data.ok) {
        Logger.log('Failture while uploading image to S3', { preSignedUrl });
        throw new Error('error while uploading image to S3');
      }

      return;
    } catch (error) {
      Logger.log('error while uploading image to S3', { error });
      throw new Error('error while uploading image to S3');
    }
  }

  /**
   * bug report form submission.
   * @param payload
   */
  async SubmitBugReportForm(payload: BugReportFormMetadata) {
    try {
      const data = {
        ...payload,
        ...this.getClientContext(),
        bugId: this.getBugReportId() || '',
        requestedAt: new Date(),
      };

      const url = this.getUrl('public/v1/brt/response');
      await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ data: await this.encrypt(JSON.stringify(data)) }),
      });

      this.resetBugReportId();
    } catch (error) {
      Logger.log('error while submitting bug report form', { error });
    }
  }
}
